import axios from "axios";

// Get API base URL from environment or use default
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Utility function to get user's timezone
const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return "UTC";
  }
};

// Request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    // Add authentication header if session exists for current event
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    const eventSlug = pathParts[1]; // /e/:eventSlug format

    if (eventSlug) {
      const sessionKey = `attendee_session_${eventSlug}`;
      const session = localStorage.getItem(sessionKey);
      if (session) {
        try {
          const sessionData = JSON.parse(session);
          if (sessionData.session_token) {
            config.headers.Authorization = `Bearer ${sessionData.session_token}`;
          }
        } catch (error) {
          localStorage.removeItem(sessionKey);
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      const pathParts = window.location.pathname.split("/").filter(Boolean);
      const eventSlug = pathParts[1]; // /e/:eventSlug format
      if (eventSlug) {
        localStorage.removeItem(`attendee_session_${eventSlug}`);
      }
      // Redirect to login if not already on auth pages
      const currentPath = window.location.pathname;
      if (!currentPath.includes("/login") && !currentPath.includes("/verify")) {
        if (eventSlug) {
          window.location.href = `/e/${eventSlug}/login`;
        }
      }
    }

    // Handle network errors
    if (!error.response) {
      error.message = "Network error - please check your connection";
    }

    return Promise.reject(error);
  }
);

// Public API endpoints (no authentication required)
export const publicApi = {
  // Get event data by slug (for install page only)
  getEvent: async (eventSlug) => {
    const response = await api.get(`/public/events/${eventSlug}`);
    return response.data;
  },

  // Send verification code
  sendVerificationCode: async (eventSlug, email) => {
    const response = await api.post(`/v1/events/${eventSlug}/auth/send_code`, {
      email: email,
    });
    return response.data;
  },

  // Verify code
  verifyCode: async (eventSlug, email, code) => {
    const response = await api.post(
      `/v1/events/${eventSlug}/auth/verify_code`,
      {
        email: email,
        code: code,
      }
    );
    return response.data;
  },

  // Verify API key for preview mode
  verifyApiKey: async (eventSlug, apiKey, eventId) => {
    const response = await api.post(
      `/v1/events/${eventSlug}/auth/verify_api_key`,
      {},
      {
        headers: {
          "X-API-KEY": apiKey,
          "X-EVENT-ID": eventId,
        },
      }
    );
    return response.data;
  },
};

// Authenticated API endpoints
export const authenticatedApi = {
  // Get event data by slug (requires authentication)
  getEvent: async (eventSlug) => {
    const response = await api.get(`/v1/events/${eventSlug}`);
    return response.data;
  },

  // Fetch specific objects by their IDs and types
  fetchObjects: async (eventSlug, dataArray) => {
    const response = await api.post(`/v1/events/${eventSlug}/fetch_objects`, {
      data: dataArray,
    });
    return response.data;
  },

  // Get speakers for a specific section
  getSpeakers: async (eventSlug, sectionId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/sections/${sectionId}/speakers`
    );
    return response.data;
  },

  // Get individual speaker
  getSpeaker: async (eventSlug, sectionId, speakerId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/sections/${sectionId}/speakers/${speakerId}`
    );
    return response.data;
  },

  // Get sessions for a specific section
  getSessions: async (eventSlug, sectionId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/sections/${sectionId}/sessions`
    );
    return response.data;
  },

  // Get individual session
  getSession: async (eventSlug, sectionId, sessionId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/sections/${sectionId}/sessions/${sessionId}`
    );
    return response.data;
  },

  // Get sponsors for a specific section
  getSponsors: async (eventSlug, sectionId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/sections/${sectionId}/sponsors`
    );
    return response.data;
  },

  // Get individual sponsor
  getSponsor: async (eventSlug, sectionId, sponsorId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/sections/${sectionId}/sponsors/${sponsorId}`
    );
    return response.data;
  },

  // Get exhibitors for a specific section
  getExhibitors: async (eventSlug, sectionId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/sections/${sectionId}/exhibitors`
    );
    return response.data;
  },

  // Get individual exhibitor
  getExhibitor: async (eventSlug, sectionId, exhibitorId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/sections/${sectionId}/exhibitors/${exhibitorId}`
    );
    return response.data;
  },

  // Get items for a specific section
  getItems: async (eventSlug, sectionId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/sections/${sectionId}/items`
    );
    return response.data;
  },

  // Get individual item
  getItem: async (eventSlug, sectionId, itemId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/sections/${sectionId}/items/${itemId}`
    );
    return response.data;
  },

  // Get maps for a specific section
  getMaps: async (eventSlug, sectionId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/sections/${sectionId}/maps`
    );
    return response.data;
  },

  // Get individual map
  getMap: async (eventSlug, sectionId, mapId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/sections/${sectionId}/maps/${mapId}`
    );
    return response.data;
  },

  // Get attendees for the event
  getAttendees: async (eventSlug) => {
    const response = await api.get(`/v1/events/${eventSlug}/attendees`);
    return response.data;
  },

  // Get banners for the event
  getBanners: async (eventSlug) => {
    const response = await api.get(`/v1/events/${eventSlug}/banners`);
    return response.data;
  },

  // Get individual banner
  getBanner: async (eventSlug, bannerId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/banners/${bannerId}`
    );
    return response.data;
  },

  // Get sections for the event
  getSections: async (eventSlug) => {
    const response = await api.get(`/v1/events/${eventSlug}/sections`);
    return response.data;
  },

  // Get individual section
  getSection: async (eventSlug, sectionId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/sections/${sectionId}`
    );
    return response.data;
  },

  // Ask copilot a question (synchronous)
  askCopilot: async (
    eventSlug,
    question,
    attendeeId = null,
    timezone = null
  ) => {
    const params = {
      question: question,
      timezone: timezone || getUserTimezone(),
    };
    if (attendeeId) {
      params.attendee_id = attendeeId;
    }

    const response = await api.post(
      `/v1/events/${eventSlug}/copilot/ask`,
      params
    );
    return response.data;
  },

  // Ask copilot a question asynchronously (returns immediately)
  askCopilotAsync: async (
    eventSlug,
    question,
    attendeeId = null,
    timezone = null
  ) => {
    const params = {
      question: question,
      timezone: timezone || getUserTimezone(),
    };
    if (attendeeId) {
      params.attendee_id = attendeeId;
    }

    const response = await api.post(
      `/v1/events/${eventSlug}/copilot/ask_async`,
      params
    );
    return response.data;
  },

  // Poll for copilot response status
  pollCopilotStatus: async (
    eventSlug,
    conversationId,
    attendeeId = null,
    messageId = null
  ) => {
    const params = { conversation_id: conversationId };
    if (attendeeId) {
      params.attendee_id = attendeeId;
    }
    if (messageId) {
      params.message_id = messageId;
    }

    const response = await api.get(
      `/v1/events/${eventSlug}/copilot/poll_status`,
      { params }
    );
    return response.data;
  },

  // Get conversation history
  getConversationHistory: async (eventSlug, attendeeId) => {
    const response = await api.get(`/v1/events/${eventSlug}/copilot/history`, {
      params: { attendee_id: attendeeId },
    });
    return response.data;
  },

  // Clear conversation history
  clearConversationHistory: async (eventSlug, attendeeId) => {
    const response = await api.delete(
      `/v1/events/${eventSlug}/copilot/history`,
      {
        params: { attendee_id: attendeeId },
      }
    );
    return response.data;
  },

  // Favorites API methods
  getFavorites: async (eventSlug) => {
    const response = await api.get(`/v1/events/${eventSlug}/favorites`);
    return response.data;
  },

  createFavorite: async (eventSlug, favoritableType, favoritableId) => {
    const response = await api.post(`/v1/events/${eventSlug}/favorites`, {
      favoritable_type: favoritableType,
      favoritable_id: favoritableId,
    });
    return response.data;
  },

  deleteFavorite: async (eventSlug, favoriteId) => {
    const response = await api.delete(
      `/v1/events/${eventSlug}/favorites/${favoriteId}`
    );
    return response.data;
  },

  // Stream chat API methods
  getStreamToken: async (eventSlug) => {
    const response = await api.get(`/v1/events/${eventSlug}/stream/token`);
    return response.data;
  },

  upsertStreamUser: async (eventSlug, attendeeId) => {
    const response = await api.post(
      `/v1/events/${eventSlug}/stream/upsert_user`,
      {
        attendee_id: attendeeId,
      }
    );
    return response.data;
  },

  syncProfileToStream: async (eventSlug) => {
    const response = await api.post(
      `/v1/events/${eventSlug}/stream/sync_profile`
    );
    return response.data;
  },

  // Push notification API methods
  subscribeToPushNotifications: async (eventSlug, subscription) => {
    const response = await api.post(
      `/v1/events/${eventSlug}/push-notifications/subscribe`,
      {
        subscription: subscription,
      }
    );
    return response.data;
  },

  unsubscribeFromPushNotifications: async (eventSlug) => {
    const response = await api.delete(
      `/v1/events/${eventSlug}/push-notifications/unsubscribe`
    );
    return response.data;
  },

  updatePushNotificationPreferences: async (eventSlug, enabled, types = []) => {
    const response = await api.put(
      `/v1/events/${eventSlug}/push-notifications/preferences`,
      {
        enabled,
        types,
      }
    );
    return response.data;
  },

  getPushNotificationStatus: async (eventSlug) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/push-notifications/status`
    );
    return response.data;
  },

  sendTestPushNotification: async (eventSlug) => {
    const response = await api.post(
      `/v1/events/${eventSlug}/push-notifications/test`
    );
    return response.data;
  },

  // Broadcast API methods
  getBroadcasts: async (eventSlug) => {
    const response = await api.get(`/v1/events/${eventSlug}/broadcasts`);
    return response.data;
  },

  getBroadcast: async (eventSlug, broadcastId) => {
    const response = await api.get(
      `/v1/events/${eventSlug}/broadcasts/${broadcastId}`
    );
    return response.data;
  },
};

export default api;
